import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowUpRight,
  CheckCircle2,
  Loader2,
  Users,
  Target,
  Calendar,
  Sparkles,
} from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { cn } from "@/platform/lib/cn";
import { adminListWaitlist, getWaitlistStats } from "@/platform/waitlist/api";
import type { PlatformTenantSlug } from "@/platform/lib/database.types";

const GATE_1_THRESHOLD = 8;

const TABS: Array<{ slug: PlatformTenantSlug | "all"; label: string; accent: string }> = [
  { slug: "all", label: "Todas", accent: "255 78% 62%" },
  { slug: "einstein", label: "PsiEinstein", accent: "255 78% 62%" },
  { slug: "usp", label: "FuvestPro USP", accent: "222 65% 50%" },
];

export function AdminWaitlistPage() {
  const [activeTab, setActiveTab] = useState<PlatformTenantSlug | "all">("all");

  const statsQuery = useQuery({
    queryKey: ["admin", "waitlist", "stats"],
    queryFn: getWaitlistStats,
  });

  const listQuery = useQuery({
    queryKey: ["admin", "waitlist", "list", activeTab],
    queryFn: () => adminListWaitlist(activeTab === "all" ? undefined : activeTab),
  });

  const stats = statsQuery.data ?? [];
  const uspStats = stats.find((s) => s.tenant === "usp");
  const einsteinStats = stats.find((s) => s.tenant === "einstein");

  return (
    <Container size="wide" className="py-12 md:py-16">
      <header className="mb-10">
        <Badge variant="accentSolid" className="gap-2">
          <Users className="size-3" aria-hidden />
          Admin · Waitlist
        </Badge>
        <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
          Lista de espera por produto
        </h1>
        <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
          Acompanhamento dos pré-cadastros e intenções de pré-pagamento. Gate 1 do FuvestPro USP
          destrava quando atinge {GATE_1_THRESHOLD}+ intenções.
        </p>
      </header>

      {/* Gate 1 progress USP */}
      <div className="grid gap-4 md:grid-cols-2 mb-10">
        <GateCard
          title="Gate 1 — FuvestPro USP"
          tenant="usp"
          accent="222 65% 50%"
          totalSignups={uspStats?.total_signups ?? 0}
          paymentIntents={uspStats?.payment_intents ?? 0}
          threshold={GATE_1_THRESHOLD}
          firstSignup={uspStats?.first_signup_at ?? null}
          latestSignup={uspStats?.latest_signup_at ?? null}
        />
        <GateCard
          title="PsiEinstein PatternLab"
          tenant="einstein"
          accent="255 78% 62%"
          totalSignups={einsteinStats?.total_signups ?? 0}
          paymentIntents={einsteinStats?.payment_intents ?? 0}
          threshold={null}
          firstSignup={einsteinStats?.first_signup_at ?? null}
          latestSignup={einsteinStats?.latest_signup_at ?? null}
        />
      </div>

      {/* Tabs */}
      <nav className="mb-6 inline-flex flex-wrap gap-1 rounded-full border border-[--color-border] bg-[color:var(--color-card)] p-1 shadow-[var(--shadow-sm)]">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.slug;
          return (
            <button
              key={tab.slug}
              type="button"
              onClick={() => setActiveTab(tab.slug)}
              className={cn(
                "rounded-full px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium transition-colors duration-150",
                isActive ? "text-white" : "text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]",
              )}
              style={isActive ? { backgroundColor: `hsl(${tab.accent})` } : undefined}
              aria-pressed={isActive}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {/* Lista */}
      {listQuery.isLoading ? (
        <div className="flex items-center gap-2 text-[color:var(--color-muted-foreground)]">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Carregando…
        </div>
      ) : (listQuery.data?.length ?? 0) === 0 ? (
        <div className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-10 text-center">
          <Users className="mx-auto size-8 text-[color:var(--color-muted-foreground)]" aria-hidden />
          <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            Ninguém na lista ainda.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[--color-border]">
          <table className="w-full text-[length:var(--text-body-sm)]">
            <thead className="bg-[color:var(--color-muted)] text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              <tr>
                <th className="px-4 py-3 text-left font-medium">#</th>
                <th className="px-4 py-3 text-left font-medium">Nome</th>
                <th className="px-4 py-3 text-left font-medium">Produto</th>
                <th className="px-4 py-3 text-left font-medium">Cadastro</th>
                <th className="px-4 py-3 text-left font-medium">Pré-pago</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.data?.map((entry, idx) => (
                <tr
                  key={entry.id}
                  className="border-t border-[--color-border] hover:bg-[color:var(--color-muted)]/30"
                >
                  <td
                    data-tabular="true"
                    className="px-4 py-3 font-mono text-[color:var(--color-muted-foreground)]"
                  >
                    {idx + 1}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {entry.display_name ?? entry.full_name ?? "—"}
                    <span className="block text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                      {entry.user_id.slice(0, 8)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant="outline"
                      className="gap-1.5"
                      style={{
                        borderColor: `hsl(${entry.tenant === "usp" ? "222 65% 50%" : "255 78% 62%"} / 0.4)`,
                        color: `hsl(${entry.tenant === "usp" ? "222 70% 35%" : "255 75% 45%"})`,
                      }}
                    >
                      <span
                        aria-hidden
                        className="size-1.5 rounded-full"
                        style={{
                          backgroundColor: `hsl(${entry.tenant === "usp" ? "222 65% 50%" : "255 78% 62%"})`,
                        }}
                      />
                      {entry.tenant === "usp" ? "FuvestPro USP" : "PsiEinstein"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-[color:var(--color-muted-foreground)]">
                    {entry.created_at
                      ? format(parseISO(entry.created_at), "d MMM yyyy, HH:mm", { locale: ptBR })
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {entry.signal_payment_intent ? (
                      <span className="inline-flex items-center gap-1.5">
                        <CheckCircle2 className="size-4" style={{ color: "hsl(155 60% 38%)" }} aria-hidden />
                        <span className="font-medium">
                          R$ {Number(entry.signal_amount_brl ?? 50).toFixed(2).replace(".", ",")}
                        </span>
                      </span>
                    ) : (
                      <span className="text-[color:var(--color-muted-foreground)]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Container>
  );
}

function GateCard({
  title,
  accent,
  totalSignups,
  paymentIntents,
  threshold,
  firstSignup,
  latestSignup,
}: {
  title: string;
  tenant: PlatformTenantSlug;
  accent: string;
  totalSignups: number;
  paymentIntents: number;
  threshold: number | null;
  firstSignup: string | null;
  latestSignup: string | null;
}) {
  const pct = threshold ? Math.min(100, (paymentIntents / threshold) * 100) : null;
  const gateOpen = threshold !== null && paymentIntents >= threshold;

  return (
    <article
      className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6 shadow-[var(--shadow-sm)]"
      style={{ "--card-accent": `hsl(${accent})` } as React.CSSProperties}
    >
      <header className="flex items-center justify-between gap-3">
        <h2 className="font-display text-[length:var(--text-body-lg)] font-semibold">{title}</h2>
        {gateOpen && (
          <Badge variant="accentSolid" className="gap-1.5">
            <Sparkles className="size-3" aria-hidden />
            Gate destravado
          </Badge>
        )}
      </header>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-[--color-border] p-3">
          <p className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            Pré-cadastros
          </p>
          <p
            data-tabular="true"
            className="mt-1 font-display text-[length:var(--text-display-md)] font-semibold leading-none"
            style={{ color: `hsl(${accent})` }}
          >
            {totalSignups}
          </p>
        </div>
        <div className="rounded-xl border border-[--color-border] p-3">
          <p className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            Com intenção R$
          </p>
          <p
            data-tabular="true"
            className="mt-1 font-display text-[length:var(--text-display-md)] font-semibold leading-none"
            style={{ color: `hsl(${accent})` }}
          >
            {paymentIntents}
            {threshold && (
              <span className="text-[length:var(--text-body-sm)] font-normal text-[color:var(--color-muted-foreground)]">
                /{threshold}
              </span>
            )}
          </p>
        </div>
      </div>

      {pct !== null && (
        <div className="mt-4">
          <div className="h-2 overflow-hidden rounded-full bg-[color:var(--color-muted)]">
            <div
              className="h-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: `hsl(${accent})` }}
            />
          </div>
          <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
            <Target className="mr-1 inline size-3" aria-hidden />
            Faltam <strong>{Math.max(0, (threshold ?? 0) - paymentIntents)}</strong> intenções para
            destravar Gate 1.
          </p>
        </div>
      )}

      <footer className="mt-4 flex items-center gap-4 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
        <span className="inline-flex items-center gap-1">
          <Calendar className="size-3" aria-hidden />
          {firstSignup
            ? `1º: ${format(parseISO(firstSignup), "d/MMM", { locale: ptBR })}`
            : "Sem cadastros"}
        </span>
        {latestSignup && (
          <span className="inline-flex items-center gap-1">
            <ArrowUpRight className="size-3" aria-hidden />
            Último: {format(parseISO(latestSignup), "d/MMM HH:mm", { locale: ptBR })}
          </span>
        )}
      </footer>
    </article>
  );
}
